/**
 * Where one MA row stands for the reviewer: which WK brand it is paired with
 * (if any), how that pairing came about, and which list chip it falls under.
 * Pure — combines the auto-match with this session's decisions.
 */
import type { WatchBrand } from '../../types';
import {
  findBestWkMatch,
  isConfidentMatch,
  type BrandMatch,
  type MaMatchInput,
  type WkMatchIndex,
} from './matching';
import type { ListStatusFilter } from './sessionCache';
import type { SessionStatus } from './sessionStatus';

export type ProposalCategory = Exclude<ListStatusFilter, 'all'>;

export interface ProposalMatch {
  /** The WK brand an Update would write to; null means Create. */
  wkBrand: WatchBrand | null;
  /** `session` = the reviewer chose or wrote it this session; `auto` = heuristic. */
  source: 'session' | 'auto' | null;
  /** The heuristic's own answer, kept even when the session overrides it. */
  auto: BrandMatch | null;
  /**
   * The pairing is a containment-only suggestion the reviewer has not yet
   * confirmed. Update stays disabled until they do.
   */
  needsConfirm: boolean;
  category: ProposalCategory;
}

export function resolveProposalMatch(params: {
  ma: MaMatchInput;
  index: WkMatchIndex;
  wkById: ReadonlyMap<string, WatchBrand>;
  status: SessionStatus | undefined;
  autoMatchRejected: boolean;
}): ProposalMatch {
  const { ma, index, wkById, status, autoMatchRejected } = params;
  const auto = findBestWkMatch(ma, index);

  if (status?.kind === 'dismissed') {
    return {
      wkBrand: autoMatchRejected ? null : (auto?.wkBrand ?? null),
      source: auto && !autoMatchRejected ? 'auto' : null,
      auto,
      needsConfirm: false,
      category: 'session-dismissed',
    };
  }

  const sessionBrand = status?.wkBrandId ? wkById.get(status.wkBrandId) : undefined;
  if (status && sessionBrand) {
    return {
      wkBrand: sessionBrand,
      source: 'session',
      auto,
      needsConfirm: false,
      category:
        status.kind === 'created'
          ? 'session-created'
          : status.kind === 'updated'
            ? 'session-updated'
            : 'matched',
    };
  }

  if (auto && !autoMatchRejected) {
    return {
      wkBrand: auto.wkBrand,
      source: 'auto',
      auto,
      needsConfirm: !isConfidentMatch(auto),
      category: 'matched',
    };
  }

  return { wkBrand: null, source: null, auto, needsConfirm: false, category: 'unmatched' };
}

const VIA_LABEL: Record<BrandMatch['via'], string> = {
  stamp: 'already imported',
  slug: 'slug match',
  name: 'name match',
  alias: 'alias match',
  containment: 'possible match',
};

export function describeMatch(match: ProposalMatch): string {
  if (!match.wkBrand) return 'No WK brand';
  if (match.source === 'session') return 'chosen this session';
  return match.auto ? VIA_LABEL[match.auto.via] : '';
}
